// In src/DriverSupport.js

import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, query, onSnapshot, orderBy } from "firebase/firestore";
import { FaSpinner, FaPaperPlane, FaDownload } from 'react-icons/fa';

// --- Ticket Detail Modal Component ---
const TicketDetailModal = ({ ticket, onClose, onReply, processing }) => {
    const [replyMessage, setReplyMessage] = useState('');
    const [newStatus, setNewStatus] = useState(ticket.status);

    const handleReply = () => {
        if (!replyMessage) return;
        onReply(ticket.id, replyMessage, newStatus);
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{maxWidth: '700px'}}>
                <div className="modal-header">
                    <h3>Ticket Details [#{ticket.id.substring(0, 6)}]</h3>
                    <button onClick={onClose} className="modal-close-btn">&times;</button>
                </div>
                <div className="modal-body">
                    {/* Ticket Info */}
                    <div className="detail-grid">
                        <div className="detail-item"><strong>Driver Name:</strong> {ticket.firstName} {ticket.lastName}</div>
                        <div className="detail-item"><strong>Driver Email:</strong> {ticket.email}</div>
                        <div className="detail-item"><strong>Category:</strong> {ticket.category}</div>
                        <div className="detail-item"><strong>Submitted:</strong> {ticket.createdAt?.toDate().toLocaleString()}</div>
                    </div>
                    {ticket.attachmentUrl && (
                        <div className="attachment-link">
                            <a href={ticket.attachmentUrl} target="_blank" rel="noopener noreferrer">
                                <FaDownload /> View Attached Image
                            </a>
                        </div>
                    )}
                    <hr />
                    {/* Conversation History */}
                    <div className="history-container">
                        {ticket.history?.map((item, index) => (
                            <div key={index} className={`history-item ${item.author === 'driver' ? 'user-message' : 'admin-reply'}`}>
                                <p className="history-content">{item.content}</p>
                                <p className="history-meta">
                                    {item.author === 'driver' ? ticket.firstName : item.author} - {item.timestamp?.toDate().toLocaleString()}
                                </p>
                            </div>
                        ))}
                    </div>
                    <hr />
                    {/* Reply Section */}
                    <div className="reply-section">
                        <h4>Reply to Ticket</h4>
                        <textarea
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            rows="5"
                            placeholder="Type your reply here..."
                            className="form-input"
                        />
                        <div className="reply-actions">
                            <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="form-input">
                                <option value="new">Mark as New</option>
                                <option value="in_progress">Mark as In Progress</option>
                                <option value="closed">Mark as Closed</option>
                            </select>
                            <button onClick={handleReply} disabled={processing} className="btn btn-primary">
                                {processing ? <FaSpinner className="animate-spin"/> : <FaPaperPlane />}
                                Send Reply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main Component ---
export default function DriverSupport() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        const q = query(collection(db, "driverSupportTickets"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setTickets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);
    
    const handleReply = async (ticketId, replyMessage, newStatus) => {
        setProcessing(true);
        try {
            const user = auth.currentUser;
            if (!user) throw new Error("Not logged in.");
            const idToken = await user.getIdToken(true);

            const response = await fetch("https://replytodriverticket-s7dx7q362a-uc.a.run.app", { // Replace with your actual function URL
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({ ticketId, replyMessage, newStatus })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to send reply.");
            }
            alert("Reply sent successfully!");
            setSelectedTicket(null);
        } catch (error) {
            alert(`Error: ${error.message}`);
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return <p className="loading-text">Loading driver support tickets...</p>;

    return (
        <div className="card">
            <h2 className="page-header">Driver Support Tickets</h2>
            <p className="page-subheader">View and respond to inquiries from your active drivers.</p>
            
            {selectedTicket && (
                <TicketDetailModal 
                    ticket={selectedTicket}
                    onClose={() => setSelectedTicket(null)}
                    onReply={handleReply}
                    processing={processing}
                />
            )}

            <table className="table">
                <thead>
                    <tr>
                        <th>Status</th>
                        <th>Driver Name</th>
                        <th>Category</th>
                        <th>Date</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {tickets.map(ticket => (
                        <tr key={ticket.id}>
                            <td><span className={`status-badge status-${ticket.status}`}>{ticket.status}</span></td>
                            <td>{ticket.firstName} {ticket.lastName}</td>
                            <td>{ticket.category}</td>
                            <td>{ticket.createdAt?.toDate().toLocaleString()}</td>
                            <td>
                                <button onClick={() => setSelectedTicket(ticket)} className="btn btn-view">
                                    View & Reply
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}